import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { initAllStores } from "../utils/cache";
import User from "../models/User";
import type Order from "../models/Order";
import { formatDateTime, getTodayRange } from "../utils/date";
import {
  computeBookingSummary, mergeSummaries,
  groupOrderItemsByType, getExpenseByDate
} from "../utils/sales-summary";
import { useNetwork } from "../context/NetworkContext"; // ✅ import context
import { useModal } from "../context/ConfirmModalContext";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { syncBillHistory } from "../utils/sync";
import BookedRoom from "../models/BookedRoom";
// import { shuffle } from "../utils/helper-function";
import { Table } from "../components/Table";
import { RecentTransaction } from "../types/sales";
import Permission from "../models/Permission";
import { checkAccess } from "../utils/access";
import { orderItemsTotal } from "../utils/orders-helper";
import SalesSummaryModal from "../Modal/accounting/SaleSummary";
import Expenditure from "../models/Expenditure";
import { TerminalBadge } from "../components/TerminalBadge";
import { terminal } from "../utils/user-helper";


export const DashboardPage = () => {
  const navigate = useNavigate();
  const { isOnline } = useNetwork(); // ✅ detect internet status
  const { openModal, closeModal } = useModal();

  const [terminalId] = useState(() => terminal());

  const [showAmount, setShowAmount] = useState(false);
  const [showPending, setShowPending] = useState(false);
  // const [toggleSales, setToggleSales] = useState(false);

  const [access, setAccess] = useState({
    sales: false, bookings: false, orders: false
  });

  const [statData, setStatData] = useState<{
    userName: string;
    availableRoom: number;
    orderCount: number;
    vendorCount: number,
    guestCount: number;
    todaySales: RecentTransaction[];
  }>({
    userName: "",
    availableRoom: 0,
    orderCount: 0,
    vendorCount: 0,
    guestCount: 0,
    todaySales: [],
  });

  const [accountSummary, setAccountSummary] = useState({} as any);

  const [currentTime, setCurrentTime] = useState(new Date());
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formattedDate = currentTime.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const formattedTime = currentTime.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });


  useEffect(() => {
    const init = async () => {
      const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming;

      // 🔄 Step 1: Sync from server if refreshed
      if (navigation?.type === "reload" && navigator.onLine) {
        // console.log("Refreshing page: syncing data from server...")

        // console.log("✅ Sync complete");
      }

      // 📊 Step 2: Now load stats (after sync completes)
      const cache = await initAllStores();
      const user: User | null = await cache.getBy("data", { id: "user" });
      const availableRoomCount = await cache.countBy("rooms", {
        isOccupied: false, terminal: terminalId
      });
      const vendorCount = await cache.countBy("vendors");

      if (!user) {
        return openModal(
          "Session Expired ⚠️",
          "We couldn’t find your user information. Please log in again to continue.",
          () => {
            closeModal();
            window.location.href = "/login";
          },
          false,
          "Login Again"
        );
      }

      // Update user access.
      if (user?.permissions?.length) {
        const updatedAccess = { sales: false, bookings: false, orders: false };

        user.permissions.forEach((perm: Permission) => {
          if (perm.type === "view" && perm.value === true) {
            if (perm.name === "sales") updatedAccess.sales = true;
            if (perm.name === "bookings") updatedAccess.bookings = true;
            if (perm.name === "orders") updatedAccess.orders = true;
          }
        });

        setAccess(updatedAccess);
      }

      await syncBillHistory({ bookedRooms: [], orders: [] });
      const { start, end } = getTodayRange();
      const todayBookings: BookedRoom[] = await cache.getAllBy(
        "bookedRooms", { terminal: terminalId }, "createdAt", "asc", { field: "createdAt", start, end }
      );

      const todayOrders: Order[] = await cache.getAllBy(
        "orders", { terminal: terminalId }, "createdAt", "asc", { field: "createdAt", start, end }
      );

      const { foods, drinks, games, laundries } = await groupOrderItemsByType(todayOrders);
      const bookingSummary = computeBookingSummary(todayBookings);
      const totalSummary = mergeSummaries({
        foods: foods.summary,
        drinks: drinks.summary,
        laundries: laundries.summary,
        games: games.summary,
        rooms: bookingSummary
      });

      const today = (new Date()).toDateString();
      const expenseData = await getExpenseByDate(terminalId, { start: today, end: today });
      if (expenseData) {
        const { expenditures } = expenseData;
        const totalExpenditure = (expenditures as Expenditure[] ?? []).reduce(
          (sum, e) => sum + e.amount,
          0 // initial value
        );

        totalSummary.expenses = totalExpenditure;
      }
      // console.log(totalSummary)
      setAccountSummary(totalSummary)

      const mapOrders = todayOrders.map((order: Order) => ({
        id: order.id,
        customerName:
          order.customer?.name
          ?? "Lodged Guest",
        roomNo: order.bookedRoom?.room?.number ?? "N/A",
        paymentStatus: order.isPaid ? "Paid" : "Pending",
        discount: order.discount.toLocaleString(),
        time: formatDateTime(order.createdAt).split(",")[2],
        amount: orderItemsTotal(order?.orderItems).toLocaleString(),
      }));

      const mapBookings = await Promise.all(todayBookings.map(async (b: BookedRoom) => {
        const room = await cache.getBy("rooms", { id: b.roomId });
        return {
          id: b.id,
          customerName: b.guestName ?? "Lodged Guest",
          roomNo: room?.number ?? "N/A",
          paymentStatus: b.isPaid ? "Paid" : "Pending",
          discount: b.discount.toLocaleString(),
          time: formatDateTime(b?.createdAt).split(",")[2],
          amount: b.amount.toLocaleString()
        }
      }));

      // const allTodayHistory = shuffle([...mapOrders, ...mapBookings]).slice(0, 5);
      // const sortedHistory = allTodayHistory.sort(
      //   (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()
      // );

      setStatData({
        userName: user?.userName ?? "User",
        availableRoom: availableRoomCount,
        orderCount: todayOrders.length,
        vendorCount,
        // guestCount: todayBookings.length,
        guestCount: await cache.countBy(
          "bookedRooms", {
          bookingType: "fullTime",
          // bookingTyp: "shortTime"
        }
        ),
        // salesAmount: totalSalesAmount,
        todaySales: [...mapOrders, ...mapBookings],
      });
    };

    init();
  }, []);

  return (
    <div className="flex-1 p-4 md:p-6 space-y-6 overflow-y-auto">
      <div className="bg-white shadow-md rounded-2xl p-6 flex flex-col md:flex-row justify-between items-start md:items-center">

        <TerminalBadge />

        {/* Welcome Card */}
        <div className="space-y-1">
          <h1 className="text-2xl font-bold">
            Hello <span className="text-blue-600">{statData.userName}</span> 👋
          </h1>
          <p className="text-gray-600">Welcome back to your dashboard</p>
        </div>

        <div className="mt-4 md:mt-0 flex items-center space-x-3">
          <div className="flex flex-col text-right">
            <span className="text-gray-700 font-medium">{formattedDate}</span>
            <span className="text-gray-500 text-sm">{formattedTime}</span>
          </div>

          {/* ✅ Dynamic Online/Offline badge */}
          {isOnline ? (
            <span className="bg-green-100 text-green-600 px-3 py-1 rounded-full text-sm font-semibold">
              Online
            </span>
          ) : (
            <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-semibold">
              Offline
            </span>
          )}
        </div>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">


        {/* Net Sales Card */}
        <div
          title="Total received payments (Cash + POS + Transfer) excluding pending/unsettled sales and after discounts."
          className="bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md rounded-2xl p-4 transition-all hover:shadow-lg hover:scale-[1.02]"
        >
          <div className="flex justify-between items-start">
            <div>
              <p
                className="text-sm opacity-80 flex items-center gap-1  cursor-pointer "
                onClick={() => {
                  if (!checkAccess("sales", access, openModal, closeModal)) return;
                  setShowDetailsModal(true)
                }}
              >
                Gross Sales&nbsp;
                <small className="text-xs text-blue-100">(click to view)</small>
                {/* <span
                  className="text-gray-200 hover:text-gray-100 cursor-pointer"
                  title="Net Sales = Total received payments (Cash + POS + Transfer) excluding pending sales and after discounts."
                >
                  <FaInfoCircle size={14} />
                </span> */}
              </p>

              {/* Amount + Eye inline */}
              <div className="flex items-center gap-2 mt-2">
                <p className="text-2xl font-bold">
                  {showAmount ? `₦${(
                    accountSummary.grossSales 
                    // accountSummary.grossSales - accountSummary.totalDiscount
                  ).toLocaleString()}` : "******"}
                </p>

                <div
                  className="cursor-pointer"
                  onClick={() => {
                    if (!checkAccess("sales", access, openModal, closeModal)) return;
                    setShowAmount((prev) => !prev);
                  }}
                >
                  {showAmount ? (
                    <span className="w-5 h-5 text-white opacity-80 hover:opacity-100 transition">
                      {FaEyeSlash({ size: 16 })}
                    </span>
                  ) : (
                    <span className="w-5 h-5 text-white opacity-80 hover:opacity-100 transition">
                      {FaEye({ size: 16 })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="bg-gradient-to-r from-orange-500 to-orange-600 text-white
          text-white shadow-md rounded-2xl p-4 transition-all hover:shadow-lg hover:scale-[1.02]"
          title="Total unsettled payments after discounts."
        >
          <div className="flex justify-between items-start">
            <div>
              <p
                className="text-sm opacity-80 flex items-center gap-1"
                onClick={() => {
                  if (!checkAccess("sales", access, openModal, closeModal)) return;
                  // setShowDetailsModal(true)
                }}
              >
                Pending Sales&nbsp;
                {/* <span
                  className="text-gray-200 hover:text-gray-100 cursor-pointer"
                  title="Unpaid Sales = Total pending/unsettled payments after discounts."
                >
                  <FaInfoCircle size={14} />
                </span> */}
                {/* <small className="text-xs text-blue-100">(click to view)</small> */}
              </p>

              {/* Amount + Eye inline */}
              <div className="flex items-center gap-2 mt-2">
                <p className="text-2xl font-bold">
                  {showPending ? `₦${(
                    accountSummary.pending
                  ).toLocaleString()}` : "******"}
                </p>

                <div
                  className="cursor-pointer"
                  onClick={() => {
                    if (!checkAccess("sales", access, openModal, closeModal)) return;
                    setShowPending((prev) => !prev);
                  }}
                >
                  {showPending ? (
                    <span className="w-5 h-5 text-white opacity-80 hover:opacity-100 transition">
                      {FaEyeSlash({ size: 16 })}
                    </span>
                  ) : (
                    <span className="w-5 h-5 text-white opacity-80 hover:opacity-100 transition">
                      {FaEye({ size: 16 })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="bg-gradient-to-r from-green-500 to-green-600 text-white shadow-md rounded-2xl p-4 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02]"
          onClick={
            () => {
              if (!checkAccess("bookings", access, openModal, closeModal)) return;
              navigate("/rooms")
            }}
          title="All currently free rooms available for booking or assignment."
        >
          <p className="text-sm opacity-80">Rooms Available</p>
          <p className="text-2xl font-bold mt-2">{statData.availableRoom}</p>
        </div>

        <div
          className="bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-md rounded-2xl p-4 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02]"
          title="Number of guests checked in today."
          onClick={
            () => {
              navigate("/vendors")
            }}
        >
          <p className="text-sm opacity-80">Vendors</p>
          <p className="text-2xl font-bold mt-2">{statData.vendorCount}</p>
        </div>
        {/* 
        <div
          className="bg-gradient-to-r from-pink-500 to-pink-600 text-white shadow-md rounded-2xl p-4 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02]"
          onClick={() => navigate("/orders", { state: { showHistory: true } })}
        >
          <p className="text-sm opacity-80">Orders</p>
          <p className="text-2xl font-bold mt-2">{statData.orderCount}</p>
        </div> */}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            className="bg-white shadow-md rounded-2xl p-6 hover:shadow-lg transition"
            onClick={() => {
              if (!checkAccess("bookings", access, openModal, closeModal)) return;
              navigate("/rooms/book")
            }}
          >
            <h3 className="text-lg font-semibold text-blue-600">Book Room</h3>
            <p className="text-sm text-gray-500 mt-2">Quickly assign a room to a guest</p>
          </button>

          <button
            className="bg-white shadow-md rounded-2xl p-6 hover:shadow-lg transition-all hover:shadow-lg hover:scale-[1.02]"
            onClick={
              () => {
                if (!checkAccess("bookings", access, openModal, closeModal)) return;
                navigate("/rooms/reserve")
              }}
          >
            <h3 className="text-lg font-semibold text-green-600">Reserve Room</h3>
            <p className="text-sm text-gray-500 mt-2">Reserve a room ahead of guest arrival</p>
          </button>


          <button
            className="bg-white shadow-md rounded-2xl p-6 hover:shadow-lg transition-all hover:shadow-lg hover:scale-[1.02]"
            onClick={() => {
              if (!checkAccess("bookings", access, openModal, closeModal)) return;
              navigate("/rooms", { state: { category: "Occupied" } })
            }}
          >
            <h3 className="text-lg font-semibold text-pink-600">Check Out</h3>
            <p className="text-sm text-gray-500 mt-2">
              Complete payment & free up rooms
            </p>
          </button>

          {/* <button
            className="bg-white shadow-md rounded-2xl p-6 hover:shadow-lg transition-all hover:shadow-lg hover:scale-[1.02]"
            onClick={() => {
              if (!checkAccess("orders", access, openModal, closeModal)) return;
              navigate("/service/foods")
            }}
          >
            <h3 className="text-lg font-semibold text-purple-600">Add Order</h3>
            <p className="text-sm text-gray-500 mt-2">
              Add food, drinks, or services to guest bill
            </p>
          </button> */}
          <button
            className="bg-white shadow-md rounded-2xl p-6 hover:shadow-lg transition-all hover:scale-[1.02]"
            onClick={() => navigate("/roster")}
          >
            <h3 className="text-lg font-semibold text-yellow-500">Staff Roster</h3>
            <p className="text-sm text-gray-500 mt-2">
              View the weekly schedule for all staff members
            </p>
          </button>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white shadow-md rounded-2xl overflow-x-auto">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">Recent Transactions</h3>
          {/* <button
            className="text-sm text-blue-600 hover:underline"
            onClick={() => navigate("/orders", { state: { showHistory: true } })}
          >
            View All
          </button> */}
        </div>

        <Table
          columns={[
            { key: "customerName", label: "Guest Name", responsive: "md", },
            { key: "roomNo", label: "Room", responsive: "md", },
            { key: "discount", label: "Discount", },
            { key: "amount", label: "Amount", },
            {
              key: "paymentStatus",
              label: "Status",
              getClassName: (value) =>
                value === "Paid"
                  ? "text-green-600 font-medium"
                  : "text-red-600 font-medium",
            },
          ]}
          rowsPerPage={5}
          data={statData.todaySales.map((sale) => ({
            ...sale,
            id: sale.id ?? "",
          }))}
        // emptyMessage="No recent transactions"
        />

        <SalesSummaryModal
          show={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          accountSummary={accountSummary}
        />

      </div>
    </div>
  );
};
